import { useMemo } from "react";
import {
  Alert,
  Badge,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { loadBranchCatalog, loadCatalog, type BiomarkerDetail } from "@/api/catalogs";
import { AppLayout } from "@/components/AppLayout";
import { t } from "@/i18n";

/**
 * Phase 3 (ADR-0040 read surfaces) — one biomarker's read-only detail.
 *
 * onboarding-api has no single-biomarker endpoint, so this derives the biomarker
 * from the catalog response (React Query caches `["catalog", org]`, so arriving
 * from the catalog detail is a cache hit; a deep link loads the catalog once).
 * A dedicated `GET /api/v1/catalogs/{org}/biomarker/{symbol}` is a backend
 * follow-up if deep-link cost matters.
 */

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <Group gap="xs" wrap="nowrap" align="baseline">
      <Text size="sm" c="dimmed" w={160} style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm">{children}</Text>
    </Group>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Title order={3}>{title}</Title>
        {children}
      </Stack>
    </Paper>
  );
}

export function BiomarkerDetailPage(): JSX.Element {
  const {
    org = "",
    branch,
    symbol = "",
  } = useParams<{
    org: string;
    branch?: string;
    symbol: string;
  }>();

  const { data, isPending, isError, error } = useQuery({
    queryKey: branch ? ["catalog", org, "branch", branch] : ["catalog", org],
    queryFn: () => (branch ? loadBranchCatalog(org, branch) : loadCatalog(org)),
  });

  const biomarker: BiomarkerDetail | undefined = useMemo(
    () => data?.biomarkers.find((b) => b.name === symbol),
    [data, symbol],
  );

  const backPath = branch
    ? `/catalogs/${encodeURIComponent(org)}/branch/${encodeURIComponent(branch)}`
    : `/catalogs/${encodeURIComponent(org)}`;

  return (
    <AppLayout
      breadcrumbs={[
        { label: t("catalogs.title"), to: "/catalogs" },
        { label: branch ? `${org} / ${branch}` : org, to: backPath },
        { label: symbol },
      ]}
    >
      {isPending ? (
        <Center py="xl">
          <Loader aria-label={t("biomarker.loading")} />
        </Center>
      ) : isError ? (
        <Alert color="red" title={t("biomarker.error_title")}>
          {error instanceof Error ? error.message : t("catalogs.error_generic")}
        </Alert>
      ) : biomarker === undefined ? (
        <Alert color="yellow" title={t("biomarker.not_found_title")}>
          {t("biomarker.not_found_body")}{" "}
          <Text span ff="monospace">
            {symbol}
          </Text>
        </Alert>
      ) : (
        <BiomarkerBody b={biomarker} />
      )}
    </AppLayout>
  );
}

function BiomarkerBody({ b }: { b: BiomarkerDetail }): JSX.Element {
  const cc = b.clinical_context;
  return (
    <Stack gap="md">
      <Group gap="sm" align="baseline">
        <Text ff="monospace" fw={600}>
          {b.name}
        </Text>
        <Title order={1}>{b.display}</Title>
        {b.members.length > 0 && (
          <Badge color="grape" variant="light">
            {t("catalog_detail.grouper")}
          </Badge>
        )}
        {b.calculated_from.length > 0 && (
          <Badge color="cyan" variant="light">
            {t("biomarker.calculated")}
          </Badge>
        )}
      </Group>

      <Section title={t("biomarker.classification")}>
        <Field label={t("catalog_detail.col_abbrev")}>{b.abbreviation ?? "—"}</Field>
        <Field label={t("biomarker.variable_name")}>{b.variable_name ?? "—"}</Field>
        <Field label={t("catalog_detail.col_panel")}>{b.panel_name ?? "—"}</Field>
        <Field label={t("biomarker.sample_type")}>{b.sample_type ?? "—"}</Field>
        <Field label={t("biomarker.coverage_type")}>{b.coverage_type ?? "—"}</Field>
        <Field label={t("biomarker.categories")}>
          {b.allowed_categories.length > 0 ? (
            <Group gap={4}>
              {b.allowed_categories.map((c) => (
                <Badge key={c} variant="outline" size="sm">
                  {c}
                </Badge>
              ))}
            </Group>
          ) : (
            "—"
          )}
        </Field>
        {b.calculated_from.length > 0 && (
          <Field label={t("biomarker.calculated_from")}>{b.calculated_from.join(", ")}</Field>
        )}
        {b.members.length > 0 && (
          <Field label={t("biomarker.members")}>{b.members.join(", ")}</Field>
        )}
      </Section>

      <Section title={t("biomarker.codes")}>
        <Field label="LOINC">{b.codes.loinc ?? "—"}</Field>
        <Field label="SNOMED">{b.codes.snomed ?? "—"}</Field>
        <Field label={t("biomarker.secondary_loinc")}>
          {b.codes.secondary_loinc.length > 0 ? b.codes.secondary_loinc.join(", ") : "—"}
        </Field>
      </Section>

      {b.units.length > 0 && (
        <Section title={t("biomarker.units")}>
          <Table withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("biomarker.unit_code")}</Table.Th>
                <Table.Th>{t("biomarker.unit_canonical")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {b.units.map((u) => (
                <Table.Tr key={u.code}>
                  <Table.Td>
                    <Text ff="monospace" size="sm">
                      {u.code}
                    </Text>
                  </Table.Td>
                  <Table.Td>{u.is_canonical ? t("biomarker.canonical") : "—"}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Section>
      )}

      {(cc.combined_what_why !== null || cc.how_custom !== null) && (
        <Section title={t("biomarker.content")}>
          {cc.combined_what_why !== null && (
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                {t("biomarker.what_why")}
              </Text>
              <Text size="sm" style={{ whiteSpace: "pre-line" }}>
                {cc.combined_what_why}
              </Text>
            </Stack>
          )}
          {cc.how_custom !== null && (
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                {t("biomarker.how")}
              </Text>
              <Text size="sm" style={{ whiteSpace: "pre-line" }}>
                {cc.how_custom}
              </Text>
            </Stack>
          )}
        </Section>
      )}
    </Stack>
  );
}
